import FuturePricingFilter from "@shared/svelte/Components/ProductLines/FuturePricingFilter.svelte";
import InStockFilter from "@shared/svelte/Components/ProductLines/InStockFilter.svelte";
import RangeFilter from "@shared/svelte/Components/ProductLines/RangeFilter.svelte";

import type { FiltersType, FilterTypesType, SelectedFiltersType } from "@shared/types/Filters";

export const allFilterTypes: FilterTypesType[] = [
  {
    id: "previously_ordered",
    name: "Previously ordered",
    param: "po",
    key: "previously_ordered",
    enable: true,
    featured: true,
  },
  { id: "deals", name: "Deals", param: "dl", key: "has_discounts", enable: true, featured: true },
  {
    id: "my_distributors",
    name: "My Distributors",
    param: "md",
    key: "my_distributors",
    enable: true,
    featured: true,
  },
  { id: "distributor", name: "Distributor", param: "d", key: "distributor_ids", enable: true },
  {
    id: "sale_division",
    name: "Sales Divisions",
    param: "sd",
    key: "sale_division_id",
    enable: true,
  },
  {
    id: "frontline_price",
    name: "Frontline price (per unit)",
    description: "Filter products by per unit price range",
    param: "f",
    key: "frontline_price",
    range: {
      from_placeholder: "Min",
      to_placeholder: "Max",
      is_decimal: true,
    },
    component: RangeFilter,
    single: true,
    enable: true,
  },
  {
    id: "in_stock",
    name: "in_stock",
    param: "ins",
    key: "in_stock",
    component: InStockFilter,
    enable: true,
  },
  {
    id: "future_pricing",
    name: "future_pricing",
    param: "fp",
    key: "future_pricing",
    component: FuturePricingFilter,
  },
  { id: "brandFamily", name: "Brand Family", param: "b", key: "brand_family_id", enable: true },
  { id: "manufacturer", name: "Supplier", param: "m", key: "manufacturer_id", enable: true },
  { id: "productType", name: "Product Type", param: "t", key: "product_type_id", enable: true },
  {
    id: "category",
    name: "Category",
    param: "c",
    key: "category_id",
    validate: f => f.name !== "Default",
    enable: true,
  },
  {
    id: "subcategory",
    name: "Subcategory",
    param: "s",
    key: "subcategory_id",
    validate: f => f.name !== "Default",
    enable: true,
  },
  {
    id: "tag",
    name: "Feature",
    param: "fe",
    key: "tag_id",
    enable: true,
  },
  {
    id: "country",
    name: "Country",
    param: "co",
    key: "country_id",
    enable: true,
  },
  {
    id: "region",
    name: "Region",
    param: "re",
    key: "region_id",
    enable: true,
  },
  {
    id: "appellation",
    name: "Appellation",
    param: "ap",
    key: "appellation_id",
    enable: true,
  },
  {
    id: "rawMaterial",
    name: "Raw Material",
    param: "rm",
    key: "raw_material_id",
    enable: true,
  },
  {
    id: "vintage_year",
    name: "Vintage",
    param: "v",
    key: "vintage_year",
    range: {
      from_placeholder: "YYYY",
      to_placeholder: "YYYY",
      max_length: 4,
    },
    component: RangeFilter,
    single: true,
    enable: true,
  },
  {
    id: "container_type",
    name: "Container Type",
    param: "n",
    key: "container_type",
    enable: true,
  },
  {
    id: "container_size",
    name: "Container Size",
    param: "z",
    key: "container_size",
    enable: true,
  },
  { id: "material", name: "Material", param: "r", key: "material", enable: true },
  {
    id: "abv",
    name: "Alcohol By Volume",
    param: "abv",
    key: "abv",
    range: {
      from_placeholder: "Min",
      to_placeholder: "Max",
      max_length: 4,
      is_decimal: true,
    },
    component: RangeFilter,
    single: true,
    enable: true,
  },
];

export const filterFiltersByEnabledRollouts = (
  filterRollouts: { id: string; enable: boolean }[],
): FilterTypesType[] => {
  return allFilterTypes
    .map(filter => {
      const filterRolloutOverride = filterRollouts.find(fr => fr.id === filter.id);
      if (filterRolloutOverride) {
        filter.enable = filterRolloutOverride.enable;
      }
      return filter;
    })
    .filter(f => f.enable);
};

const getExcludedKeysAndParamsFromStateParams = (
  stateParams: any,
  filters: FiltersType,
  excludedKeys: string[] = [],
  excludedParams: string[] = [],
): { excludedKeys: string[]; excludedParams: string[] } => {
  if (stateParams.distributors && stateParams.distributors.length > 0) {
    excludedKeys.push("distributor");
    excludedParams.push("d");
  }
  if (stateParams.brandFamilyId) {
    excludedKeys.push("brandFamily");
    excludedParams.push("b");
  }
  if (stateParams.manufacturerId) {
    excludedKeys.push("manufacturer");
    excludedParams.push("m");
  }
  if (stateParams.type) {
    excludedKeys.push("productType");
    excludedParams.push("t");
    if (
      filters && filters.product_types
        ? filters.product_types[stateParams.type] !== "Wine"
        : stateParams.type !== "2"
    ) {
      excludedKeys.push("vintage_year");
      excludedParams.push("v");
    }
  }
  if (stateParams.category) {
    excludedKeys.push("productType", "category");
    excludedParams.push("t", "c");
  }
  if (stateParams.subcategory) {
    excludedKeys.push("productType", "category", "subcategory");
    excludedParams.push("t", "c", "s");
  }
  if (stateParams.appellation) {
    excludedKeys.push("appellation");
    excludedParams.push("ap");
  }

  if (stateParams.country) {
    excludedKeys.push("country");
    excludedParams.push("co");
  }
  if (stateParams.region) {
    excludedKeys.push("country", "region");
    excludedParams.push("co", "re");
  }
  if (stateParams.tagId) {
    excludedKeys.push("tag");
    excludedParams.push("fe");
  }
  if (stateParams.rawMaterial) {
    excludedKeys.push("rawMaterial");
    excludedParams.push("rm");
  }

  return { excludedKeys, excludedParams };
};

export const filterFilterTypesFromStateParams = (
  stateParams: any,
  filterTypes: FilterTypesType[],
  filters: FiltersType,
  initialExcludedKeys: string[] = [],
): FilterTypesType[] => {
  const { excludedKeys } = getExcludedKeysAndParamsFromStateParams(
    stateParams,
    filters,
    initialExcludedKeys,
  );

  return filterTypes.filter(f => !excludedKeys.includes(f.id));
};

export const filterSelectedFiltersFromStateParams = (
  stateParams: any,
  filters: FiltersType,
  initialExcludedParams: string[] = [],
): SelectedFiltersType[] => {
  const { excludedParams } = getExcludedKeysAndParamsFromStateParams(
    stateParams,
    filters,
    [],
    initialExcludedParams,
  );

  return filters.selected_filters?.filter(f => !excludedParams.includes(f.filter.param)) || [];
};

export const shouldShowFilters = (selectedFilters: any, productsFound: boolean): boolean => {
  return selectedFilters?.length || productsFound;
};
